/*
 * Sticky add-to-basket bar for phones and desktops (snippets/sticky-atc.liquid).
 *
 * A remote control for the product form: it mirrors the page's price and Add to basket label, sets the
 * page's own "Sample collection" radio, and presses the page's own button, so everything the form does
 * (quantity, subscription, ticked offers, the added banner, the basket's clinic step) still happens.
 *
 * Events (same format and endpoint as the basket's, src 'product'; every payload carries dev: 'phone' | 'desktop'):
 *   sticky_shown   once per page view, when the bar first slides in
 *   sticky_option  an option chosen in the bar's sheet            { opt, n }
 *   pdp_add        an add that reached the basket                 { via: 'bar' | 'page', opt, sub, n }
 * n numbers events within the page view, so two real repeats in one minute aren't taken for one resent.
 * Pure helpers are exported for tests/sticky-atc.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && root.document) api.init(root.document);
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // ------------------------------------------------------------------ pure helpers

  /** The collection method of a "Sample collection" value. */
  function familyOf(value) {
    var v = String(value || '').toLowerCase();
    if (v.indexOf('walk') >= 0) return 'clinic';
    if (v.indexOf('tasso') >= 0) return 'tasso';
    if (v.indexOf('lancet') >= 0 || v.indexOf('finger') >= 0) return 'lancets';
    if (v.indexOf('home appointment') >= 0) return 'home';
    if (v.indexOf('home kit') >= 0 || v.indexOf('kit') >= 0) return 'kit';
    return 'other';
  }

  /** A short name for an option, falling back to the radio's own label without its "+ £15.00" or "£72/year". */
  function shortName(value, label) {
    switch (familyOf(value)) {
      case 'lancets': return 'Finger-prick home kit';
      case 'tasso': return 'Tasso home kit (upper arm)';
      case 'clinic': return 'Walk-in clinic';
      // "+ £15.00" surcharges and "£72/year" prices: the sheet shows the price beside the name
      default: return String(label || value || '').replace(/\s*\+\s*£[\d.,]+\s*$/, '').replace(/\s*£[\d.,]+\s*\/\s*\w+\s*$/, '').trim();
    }
  }

  /** pence -> "£59.00" */
  function money(pence) { return '£' + (Math.round(pence || 0) / 100).toFixed(2); }

  /** "+ £35.00" or "£35.00" -> 3500; anything else -> 0 */
  function feePence(text) {
    var m = /([\d]+(?:\.\d{1,2})?)/.exec(String(text || '').replace(/,/g, ''));
    return m ? Math.round(parseFloat(m[1]) * 100) : 0;
  }

  /**
   * The sheet's options from the page's radios: [{value, name, family, price, available, checked, sub}].
   * price comes from the variant whose first option is the radio's value; a clinic option notes its fee.
   */
  function buildOptions(radios, variants, feeText) {
    var fee = feePence(feeText);
    return radios.map(function (r) {
      var v = (variants || []).filter(function (x) { return x.option1 === r.value || x.title === r.value; })[0];
      var family = familyOf(r.value);
      return {
        value: r.value,
        name: shortName(r.value, r.label),
        family: family,
        price: v ? v.price : null,
        available: !r.disabled && !!(v ? v.available : true),
        checked: !!r.checked,
        sub: family === 'clinic' && fee ? '+ ' + money(fee) + ' clinic visit' : ''
      };
    });
  }

  /**
   * The sheet's options from a consultation calendar's treatment dropdown (#booking-treatment-variant: one
   * option per variant). Names come from the dropdown, prices and availability from the variant behind each.
   */
  function buildSelectOptions(selectOptions, variants) {
    return selectOptions.map(function (o) {
      var v = (variants || []).filter(function (x) { return String(x.id) === String(o.value); })[0];
      return {
        value: String(o.value),
        name: String(o.label || '').replace(/\s+/g, ' ').trim(),
        family: 'treatment',
        price: v ? v.price : null,
        available: !o.disabled && !!(v && v.available),
        checked: !!o.selected,
        sub: ''
      };
    });
  }

  /** The bar only offers a choice when more than one option can actually be picked. */
  function hasChoice(options) { return options.filter(function (o) { return o.available; }).length > 1; }

  /** FNV-1a 32-bit hash as 8 hex characters (the basket's idempotency keys). */
  function fnv1a(str) {
    var h = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return ('0000000' + h.toString(16)).slice(-8);
  }

  /** One event in the basket's format; k repeats for the same event in the same minute. */
  function makeEvent(name, base, payload, at) {
    var when = at || new Date();
    var p = {};
    for (var key in (payload || {})) if (Object.prototype.hasOwnProperty.call(payload, key)) p[key] = payload[key];
    if (base.dev) p.dev = base.dev;
    var ev = { e: name, t: when.toISOString(), sid: base.sid || '', src: 'product', method: base.method || '', li: !!base.li, g: '', p: p };
    ev.k = fnv1a(ev.sid + '|' + name + '|' + Math.floor(when.getTime() / 60000) + '|' + JSON.stringify(ev.p));
    return ev;
  }

  /** The basket's consent rule: 'always' sends, 'off' never, 'consent' asks Shopify's privacy API. */
  function consentAllows(mode, privacy) {
    if (mode === 'always') return true;
    if (mode === 'consent') { try { return !!(privacy && privacy.analyticsProcessingAllowed && privacy.analyticsProcessingAllowed()); } catch (e) { return false; } }
    return false;
  }

  /** The CSS breakpoint between the phone and desktop layouts of the bar (assets/sticky-atc.css). */
  var PHONE_QUERY = '(max-width: 768px)';
  function isPhone(win) { try { return !!(win && win.matchMedia && win.matchMedia(PHONE_QUERY).matches); } catch (e) { return false; } }

  /** Which device an event came from, by the same breakpoint, so the dashboard can report phones and desktops apart. */
  function deviceOf(win) { return isPhone(win) ? 'phone' : 'desktop'; }

  /** Shown once the page's button has scrolled fully off the top. */
  function barShouldShow(atcBottom) { return atcBottom < 0; }

  /**
   * Held adds whose add has reached the basket: those clicked when the basket held fewer items than it does
   * now. before is the count at the click (null when unknown: any reading confirms); count null = no reading.
   */
  function splitConfirmed(pending, count) {
    var ready = [], wait = [], used = 0;   // each item the basket gained confirms one add, oldest first
    pending.forEach(function (a) {
      // a product page's booking press: the appointment line grows the basket first, so only the booking's
      // own event settles it (confirmBooked)
      if (a.viaEvent) { wait.push(a); return; }
      if (count == null) { wait.push(a); return; }
      if (a.before == null) { ready.push(a); return; }
      if (count - a.before > used) { used++; ready.push(a); } else wait.push(a);
    });
    return { ready: ready, wait: wait };
  }

  /**
   * When /cart.js is read after an add (ms after the click). A consultation calendar asks for a phone number
   * before it adds, so its adds are watched for longer.
   */
  function readSchedule(calendar) { return calendar ? [1200, 2500, 5000, 9000, 20000, 45000, 90000] : [1200, 2500, 5000, 9000]; }

  /**
   * Leaving the page sends held adds unchecked for the product form (the theme may go straight to the basket).
   * A calendar's add is never sent unchecked: the customer may have left from the phone number popup.
   */
  function sendHeldOnLeave(calendar) { return !calendar; }

  /**
   * How to count one press. kind comes from BookAndAdd.pressKind (assets/book-and-add.js) on product pages:
   * 'skip' a press swallowed while a booking or the leave for the basket is under way (not an add);
   * 'calendar' a press that books first (the phone popup may stay open a while); 'plain' anything else.
   * calendarPage: a consultation calendar's page, where every press is a calendar add.
   */
  /**
   * A product page's booking finished ('bookandadd:booked', detail.add): the newest held calendar add is the
   * press that booked. With the test added it is real, so leaving sends it; book only adds no test, so it is
   * dropped. Returns the held adds that remain.
   */
  function confirmBooked(pending, added) {
    var i = -1;
    for (var k = pending.length - 1; k >= 0; k--) if (pending[k].calendar && !pending[k].sendOnLeave) { i = k; break; }
    if (i < 0) return pending;
    if (!added) return pending.filter(function (a, k) { return k !== i; });
    pending[i].sendOnLeave = true;
    return pending;
  }

  function addPlan(kind, calendarPage) {
    if (kind === 'skip') return null;
    var calendar = !!calendarPage || kind === 'calendar';
    var viaEvent = kind === 'calendar' && !calendarPage;   // settled by 'bookandadd:booked', never by reads
    return { calendar: calendar, reads: viaEvent ? [] : readSchedule(calendar), sendOnLeave: sendHeldOnLeave(calendar), viaEvent: viaEvent };
  }

  /** A booking widget's button is held until a clinic and time are picked: disabled (GP widget), or marked so (inline widget). */
  function isHeld(el) { return !!el && (!!el.disabled || el.getAttribute('aria-disabled') === 'true' || el.getAttribute('data-disabled') === 'true'); }

  /** The bar's price and fee note from the page's: "Free" for £0.00, and no fee note on its own when there is no price. */
  function barPrice(price, extra) {
    price = String(price || '').trim();
    if (!price) return { price: '', extra: '' };
    if (/^£0(\.00)?$/.test(price)) price = 'Free';
    return { price: price, extra: String(extra || '').trim() };
  }

  /** The basket's bucket for a method, so reports can join the two. */
  function methodBucket(family) { return family === 'clinic' ? 'clinic' : family === 'home' ? 'home' : family === 'other' ? '' : 'kit'; }

  // ------------------------------------------------------------------ DOM

  function init(doc) {
    var win = doc.defaultView;
    var start = function () {
      var bar = doc.querySelector('[data-sticky-atc]');
      // the product section the bar was rendered in, so a later featured-product section can't capture it
      var scope = (bar && bar.closest('.shopify-section')) || doc;
      var form = scope.querySelector('form[action*="/cart/add"]');
      var atc = form && form.querySelector('[data-product-atc], .product-form--atc-button');
      // Products with a clinic booking calendar instead of the form (TRT, weight loss, GP): the bar works the
      // calendar's own button, and until a clinic and time are picked it takes the customer up to the calendar.
      var booking = null;
      if (!atc) {
        booking = scope.querySelector('#booking-submit');
        atc = booking;
        form = booking && (booking.closest('form') || scope);
      }
      if (!bar || !form || !atc) return;
      var held = function () { return booking ? isHeld(atc) : !!atc.disabled; };
      var toCalendar = function () {
        var w = atc.closest('.clinic-booking-widget, .booking-widget') || atc;
        var header = doc.querySelector('.site-header');   // sticky: the calendar's top lands just below it
        var offset = (header ? Math.max(0, header.getBoundingClientRect().bottom) : 0) + 16;
        win.scrollTo({ top: Math.max(0, w.getBoundingClientRect().top + win.pageYOffset - offset), behavior: 'smooth' });
      };
      // ?sa_style=wide|compact previews the other look without changing the theme setting
      var styleParam = (/[?&]sa_style=(wide|compact)\b/.exec(win.location.search) || [])[1];
      if (styleParam) bar.className = bar.className.replace(/\bsticky-atc--\w+/, 'sticky-atc--' + styleParam);
      var html = doc.documentElement;
      html.classList.toggle('sticky-atc-compact', bar.classList.contains('sticky-atc--compact'));
      var sheet = doc.querySelector('[data-sa-sheet]');
      var q = function (sel, el) { return (el || bar).querySelector(sel); };
      var variants = [];
      try { variants = JSON.parse((doc.querySelector('[data-sa-variants]') || {}).textContent || '[]'); } catch (e) { variants = []; }
      var feeText = bar.getAttribute('data-clinic-fee') || '';
      var radios = function () { return Array.prototype.slice.call(form.querySelectorAll('.choose-collection-option[data-variant-option-index="0"] input[type="radio"]')); };
      var labelOf = function (r) { var l = r.closest('label') || doc.querySelector('label[for="' + r.id + '"]') || r.parentElement; return l ? l.textContent.replace(/\s+/g, ' ').trim() : r.value; };
      // a consultation calendar's treatment dropdown (TRT, weight loss) takes the place of the radios
      var treatment = booking ? scope.querySelector('#booking-treatment-variant') : null;
      var options = function () {
        if (treatment) {
          return buildSelectOptions(Array.prototype.map.call(treatment.options, function (o) {
            return { value: o.value, label: o.textContent, disabled: o.disabled, selected: o.selected };
          }), variants);
        }
        return buildOptions(radios().map(function (r) { return { value: r.value, label: labelOf(r), disabled: r.disabled, checked: r.checked }; }), variants, feeText);
      };
      var current = function () { return options().filter(function (o) { return o.checked; })[0] || null; };

      // ---- events
      var sid = '';
      var cartCount = null;   // items in the basket at the last /cart.js reading
      var seq = 0;
      var queue = [];
      var shownSent = false;
      var privacy = function () { return win.Shopify && win.Shopify.customerPrivacy; };
      var url = bar.getAttribute('data-events') || '';
      var mode = bar.getAttribute('data-events-consent') || 'off';
      var preview = !!(win.Shopify && win.Shopify.theme && win.Shopify.theme.role && win.Shopify.theme.role !== 'main');
      var flush = function () {
        if (!url || !sid || !queue.length || !consentAllows(mode, privacy())) return;
        var body = JSON.stringify(preview ? { v: 1, pv: 1, events: queue.splice(0) } : { v: 1, events: queue.splice(0) });
        try { if (win.navigator.sendBeacon && win.navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return; } catch (e) { /* fetch */ }
        try { win.fetch(url, { method: 'POST', body: body, keepalive: true, mode: 'cors', headers: { 'Content-Type': 'text/plain' } }).catch(function () {}); } catch (e2) { /* never surface */ }
      };
      var send = function (name, payload) {
        if (!url || mode === 'off') return;
        var o = current();
        queue.push(makeEvent(name, { sid: sid, dev: deviceOf(win), method: o ? methodBucket(o.family) : '', li: bar.hasAttribute('data-logged-in') }, payload));
        // events made before the session id is known get it when it arrives
        flush();
      };
      var learnSid = function () {
        return win.fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
          if (cart && typeof cart.item_count === 'number') cartCount = cart.item_count;
          var token = String((cart && cart.token) || '');   // hashed exactly as the basket does, so the session ids match
          if (!token) return;
          // a later token (the first add can create the basket) replaces the earlier one for the events still to send
          var done = function (value) { sid = value; queue.forEach(function (ev) { if (ev.sid !== value) { ev.sid = value; ev.k = fnv1a(value + '|' + ev.e + '|' + Math.floor(Date.parse(ev.t) / 60000) + '|' + JSON.stringify(ev.p)); } }); flush(); };
          if (win.crypto && win.crypto.subtle && win.TextEncoder) {
            return win.crypto.subtle.digest('SHA-256', new win.TextEncoder().encode(token)).then(function (buf) {
              done(Array.prototype.map.call(new Uint8Array(buf), function (b) { return ('0' + b.toString(16)).slice(-2); }).join('').slice(0, 32));
            });
          }
          done('f' + fnv1a(token));
        }).catch(function () {});
      };
      learnSid();
      doc.addEventListener('visitorConsentCollected', flush);
      if (mode === 'consent' && !privacy() && win.Shopify && typeof win.Shopify.loadFeatures === 'function') {
        try { win.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], flush); } catch (e) { /* stays queued */ }
      }

      // ---- render the bar from the page
      var priceText = function () {
        var el = scope.querySelector('.product-pricing [data-price-container] [data-price]') || scope.querySelector('.product-pricing [data-price-container]');
        return el ? el.textContent.replace(/Current price/i, '').replace(/\s+/g, ' ').trim() : '';
      };
      var render = function () {
        var opts = options();
        var o = current();
        var choice = hasChoice(opts);
        var price = priceText();
        var suffix = scope.querySelector('.product-pricing .price__suffix');   // "+ £35.00 clinic fee" on clinic-only tests
        var extra = suffix ? suffix.textContent.trim() : (o && o.family === 'clinic' && o.sub ? o.sub.replace(' clinic visit', ' visit') : '');
        var shown = barPrice(price, extra);
        q('[data-sa-price]').innerHTML = '';
        q('[data-sa-price]').appendChild(doc.createTextNode(shown.price));
        if (shown.extra) { var small = doc.createElement('small'); small.textContent = ' ' + shown.extra; q('[data-sa-price]').appendChild(small); }
        var optBtn = q('[data-sa-option]');
        optBtn.hidden = !choice;
        q('[data-sa-option-label]').textContent = o ? o.name : '';
        var sub = form.querySelector('input[name="selling_plan"]');
        q('[data-sa-note]').textContent = sub && sub.value ? 'Subscription' : '';
        var label = (atc.querySelector('.atc-button--text') || atc).textContent.replace(/\s+/g, ' ').trim();
        if (booking && held()) label = 'Choose a time';   // the calendar's button waits for a clinic and time
        q('[data-sa-add]').textContent = label || 'Add to basket';
        q('[data-sa-add]').disabled = booking ? false : !!atc.disabled;
        if (booking) return;
        // the clinic step can hold the page's button with aria-disabled; the bar says the same
        if (atc.getAttribute('aria-disabled') === 'true') q('[data-sa-add]').setAttribute('aria-disabled', 'true'); else q('[data-sa-add]').removeAttribute('aria-disabled');
      };

      // ---- the option sheet
      var lastFocus = null;
      var openSheet = function () {
        var list = q('[data-sa-list]', sheet);
        list.innerHTML = '';
        options().forEach(function (o) {
          var b = doc.createElement('button');
          b.type = 'button'; b.className = 'sticky-atc-opt'; b.setAttribute('role', 'radio');
          b.setAttribute('aria-checked', String(o.checked)); b.disabled = !o.available;
          b.innerHTML = '<span class="sticky-atc-opt__dot"></span><span class="sticky-atc-opt__name"></span><span class="sticky-atc-opt__price"></span>';
          b.querySelector('.sticky-atc-opt__name').textContent = o.name;
          if (o.family === 'clinic') {
            var s = doc.createElement('span'); s.className = 'sticky-atc-opt__sub'; s.textContent = 'You’ll pick your clinic and time in the basket';
            b.querySelector('.sticky-atc-opt__name').appendChild(s);
          }
          var p = b.querySelector('.sticky-atc-opt__price');
          p.textContent = o.price != null ? money(o.price) : '';
          if (o.sub) { var sm = doc.createElement('small'); sm.textContent = o.sub; p.appendChild(sm); }
          b.addEventListener('click', function () { choose(o.value); });
          list.appendChild(b);
        });
        lastFocus = doc.activeElement;
        sheet.hidden = false;
        html.classList.add('sticky-atc-sheet-open');
        var first = list.querySelector('[aria-checked="true"]') || list.querySelector('button');
        if (first) first.focus();
      };
      var closeSheet = function () {
        sheet.hidden = true;
        html.classList.remove('sticky-atc-sheet-open');
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      };
      var choose = function (value) {
        if (treatment) {
          if (treatment.value !== value) {
            treatment.value = value;   // the page's own dropdown: the calendar, the price and the address follow
            treatment.dispatchEvent(new win.Event('change', { bubbles: true }));
            send('sticky_option', { opt: 'treatment', n: ++seq });
          }
          closeSheet();
          win.setTimeout(render, 50);
          return;
        }
        var r = radios().filter(function (x) { return x.value === value; })[0];
        if (r && !r.checked) {
          r.click();   // the page's own radio: the theme updates the variant, price and panels
          send('sticky_option', { opt: familyOf(value), n: ++seq });
        }
        closeSheet();
        win.setTimeout(render, 50);
      };
      q('[data-sa-option]').addEventListener('click', openSheet);
      Array.prototype.forEach.call(sheet.querySelectorAll('[data-sa-close]'), function (el) { el.addEventListener('click', closeSheet); });
      doc.addEventListener('keydown', function (e) {
        if (sheet.hidden) return;
        if (e.key === 'Escape') { closeSheet(); return; }
        if (e.key === 'Tab') {   // keep focus inside the sheet while it is open
          var f = Array.prototype.filter.call(sheet.querySelectorAll('button'), function (b) { return !b.disabled; });
          if (!f.length) return;
          var i = f.indexOf(doc.activeElement);
          if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
          else if (!e.shiftKey && (i === -1 || i === f.length - 1)) { e.preventDefault(); f[0].focus(); }
        }
      });
      // the on-screen keyboard lifts fixed elements on iOS: the bar steps aside while a field outside it is in use
      var typing = function (el) { return isPhone(win) && !!(el && el.matches && el.matches('input:not([type=radio]):not([type=checkbox]):not([type=button]):not([type=submit]), textarea, select') && !bar.contains(el)); };
      doc.addEventListener('focusin', function (e) { html.classList.toggle('sticky-atc-typing', typing(e.target)); });
      doc.addEventListener('focusout', function () { win.setTimeout(function () { html.classList.toggle('sticky-atc-typing', typing(doc.activeElement)); }, 0); });

      // ---- adds: the bar presses the page's button; a press on the page's button is counted as 'page'
      var viaBar = false;
      // An add is held until /cart.js shows the basket has grown, so a failed add isn't counted and the event
      // carries the session id the basket will write on the order, even when this add created the basket.
      // Readings at 1.2s, 2.5s, 5s and 9s after the click; an add the basket never shows is dropped. Leaving
      // the page first (the theme may go straight to the basket) sends what is held.
      var pendingAdds = [];
      var release = function (list) { list.forEach(function (a) { send('pdp_add', a.p); }); };
      var settle = function () {
        var r = splitConfirmed(pendingAdds, cartCount);
        pendingAdds = r.wait;
        release(r.ready);
      };
      // Book and add to basket (assets/book-and-add.js) tells a booking press from a plain one; read before the press.
      var planNow = function () {
        var ba = form.bookAndAdd;
        var kind = ba && win.BookAndAdd ? win.BookAndAdd.pressKind(ba) : 'plain';
        return addPlan(kind, !!booking);
      };
      var afterAdd = function (payload, plan) {
        if (!plan) return;
        if (plan.calendar && !booking) payload.booking = true;
        payload.n = ++seq;
        var add = { before: cartCount, p: payload, sendOnLeave: plan.sendOnLeave, calendar: plan.calendar, viaEvent: plan.viaEvent };
        var READS = plan.reads;
        pendingAdds.push(add);
        READS.forEach(function (ms, i) {
          win.setTimeout(function () {
            if (pendingAdds.indexOf(add) < 0) return;
            var p = learnSid();
            (p && p.then ? p : Promise.resolve()).then(settle, settle).then(function () {
              if (i === READS.length - 1) pendingAdds = pendingAdds.filter(function (x) { return x !== add; });
            });
          }, ms);
        });
      };
      doc.addEventListener('bookandadd:booked', function (e) { pendingAdds = confirmBooked(pendingAdds, !(e && e.detail && e.detail.add === false)); });
      win.addEventListener('pagehide', function () { var heldAdds = pendingAdds; pendingAdds = []; release(heldAdds.filter(function (a) { return a.sendOnLeave; })); });
      // the consultation calendar announces a finished booking: read the basket straight away
      doc.addEventListener('qsk.cart-added', function () {
        if (!pendingAdds.length) return;
        var p = learnSid();
        (p && p.then ? p : Promise.resolve()).then(settle, settle);
      });
      // A press the calendar turns down (a booking already in the basket, no phone number, a failed booking) is
      // only written in the calendar, which is off screen behind the bar: once any phone number popup is
      // closed, take the customer to the calendar so they see what happened.
      var phonePopupOpen = function () {
        var m = doc.getElementById('bw-phone-modal');
        return !!m && m.style.display !== 'none' && !m.classList.contains('is-hidden');
      };
      var showCalendarAfterPress = function () {
        var waited = 0;
        var tick = function () {
          if (phonePopupOpen() && waited < 300000) { waited += 500; win.setTimeout(tick, 500); return; }
          toCalendar();
        };
        win.setTimeout(tick, 500);
      };
      q('[data-sa-add]').addEventListener('click', function () {
        if (booking && held()) { toCalendar(); return; }
        if (held()) return;
        var o = current();
        var plan = planNow();
        viaBar = true;
        atc.click();
        viaBar = false;
        afterAdd({ via: 'bar', opt: booking ? 'booking' : o ? o.family : '', sub: !!(form.querySelector('input[name="selling_plan"]') || {}).value }, plan);
        if (booking) showCalendarAfterPress();
      });
      atc.addEventListener('click', function () {
        if (viaBar || held()) return;
        var o = current();
        afterAdd({ via: 'page', opt: booking ? 'booking' : o ? o.family : '', sub: !!(form.querySelector('input[name="selling_plan"]') || {}).value }, planNow());
      }, true);

      // ---- show once the page's button has scrolled off the top of the screen
      html.classList.add('has-sticky-atc');
      bar.setAttribute('inert', '');
      bar.hidden = false;
      render();
      var setOn = function (on) {
        if (html.classList.contains('sticky-atc-on') === on) return;
        html.classList.toggle('sticky-atc-on', on);
        // off screen means out of the tab order and the accessibility tree too
        if (on) bar.removeAttribute('inert'); else bar.setAttribute('inert', '');
        if (!on && !sheet.hidden) closeSheet();
        if (on) { render(); if (!shownSent) { shownSent = true; send('sticky_shown', { opt: (current() || {}).family || '' }); } }
      };
      // On a consultation calendar the bar follows the whole calendar: the calendar hides its own button
      // (and so its position) while a treatment change or a new date resets the steps.
      var anchor = booking ? (atc.closest('.booking-widget') || atc) : atc;
      var check = function () { setOn(barShouldShow(anchor.getBoundingClientRect().bottom)); };
      if ('IntersectionObserver' in win) {
        new win.IntersectionObserver(function (entries) { var e = entries[0]; setOn(barShouldShow(e.isIntersecting ? 0 : e.boundingClientRect.bottom)); }).observe(anchor);
      }
      // a plain scroll check as well: older browsers, and pages whose layout shifts after load
      var ticking = false;
      win.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        (win.requestAnimationFrame || win.setTimeout)(function () { ticking = false; check(); });
      }, { passive: true });
      check();
      // keep the price, button label and subscription note in step with the page
      var pricing = scope.querySelector('.product-pricing');
      var mo = new win.MutationObserver(function () { win.clearTimeout(mo.t); mo.t = win.setTimeout(render, 30); });
      if (pricing) mo.observe(pricing, { childList: true, subtree: true, characterData: true });
      mo.observe(atc, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'data-disabled'] });
      form.addEventListener('change', function () { win.setTimeout(render, 50); });
    };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start); else start();
  }

  return { familyOf: familyOf, shortName: shortName, money: money, feePence: feePence, buildOptions: buildOptions, buildSelectOptions: buildSelectOptions, hasChoice: hasChoice, makeEvent: makeEvent, consentAllows: consentAllows, methodBucket: methodBucket, fnv1a: fnv1a, splitConfirmed: splitConfirmed, readSchedule: readSchedule, sendHeldOnLeave: sendHeldOnLeave, addPlan: addPlan, confirmBooked: confirmBooked, isHeld: isHeld, barPrice: barPrice, PHONE_QUERY: PHONE_QUERY, isPhone: isPhone, deviceOf: deviceOf, barShouldShow: barShouldShow, init: init };
});
